import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import { businessInfo } from '../mock/data';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // WhatsApp mesajı oluştur
    const whatsappMessage = `Merhaba, web sitesinden ulaşıyorum.\n\nAd Soyad: ${formData.name}\nTelefon: ${formData.phone}\nEmail: ${formData.email}\n\nMesaj: ${formData.message}`;
    const whatsappUrl = `https://wa.me/905330238300?text=${encodeURIComponent(whatsappMessage)}`;
    
    window.open(whatsappUrl, '_blank');
    
    toast.success('WhatsApp\'a yönlendiriliyorsunuz...');
    
    // Formu temizle
    setFormData({ name: '', phone: '', email: '', message: '' });
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      <Toaster position="top-right" />
      
      {/* Hero Section */}
      <section className="py-16 bg-[var(--dark-card)]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-6xl font-bold font-['Playfair_Display'] gold-gradient-text mb-6">
            İletişim
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
            Sorularınız için bize ulaşın. Size en kısa sürede dönüş yapacağız.
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-16 bg-[var(--dark-bg)]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="glass rounded-2xl p-8 space-y-6">
                <h2 className="text-2xl font-bold font-['Playfair_Display'] text-[var(--gold)] mb-6">
                  İletişim Bilgileri
                </h2>

                {/* Address */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--gold)]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={24} className="text-[var(--gold)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--gold-light)] mb-2">Adres</h3>
                    <p className="text-gray-400 leading-relaxed">{businessInfo.address}</p>
                    <p className="text-[var(--gold)] text-sm mt-1">{businessInfo.addressShort}</p>
                  </div>
                </div>

                {/* Phone Numbers */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--gold)]/10 flex items-center justify-center flex-shrink-0">
                    <Phone size={24} className="text-[var(--gold)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--gold-light)] mb-2">Telefon</h3>
                    <div className="space-y-2">
                      {businessInfo.phones.map((phone, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <a
                            href={`tel:${phone.replace(/\s/g, '')}`}
                            className="text-gray-400 hover:text-[var(--gold)] transition-colors duration-300"
                            onClick={() => window.gtag_report_conversion && window.gtag_report_conversion(`tel:${phone.replace(/\s/g, '')}`)}
                          >
                            {phone}
                          </a>
                          <a
                            href={`https://wa.me/${phone.replace(/\s/g, '').replace(/\+/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#25D366] hover:text-[#20BA5A] transition-colors duration-300"
                            title="WhatsApp"
                          >
                            <MessageCircle size={16} />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--gold)]/10 flex items-center justify-center flex-shrink-0">
                    <Mail size={24} className="text-[var(--gold)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--gold-light)] mb-2">E-posta</h3>
                    <a
                      href={`mailto:${businessInfo.email}`}
                      className="text-gray-400 hover:text-[var(--gold)] transition-colors duration-300"
                    >
                      {businessInfo.email}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-[var(--gold)]/10 flex items-center justify-center flex-shrink-0">
                    <Clock size={24} className="text-[var(--gold)]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[var(--gold-light)] mb-2">
                      Çalışma Saatleri
                    </h3>
                    <p className="text-gray-400">{businessInfo.hours}</p>
                  </div>
                </div>

                {/* Parking Info */}
                <div className="mt-6 p-4 bg-[var(--dark-bg)] rounded-lg border border-[var(--gold-dark)]">
                  <p className="text-xs text-gray-400">
                    <span className="text-[var(--gold)] font-semibold">Otopark:</span>{' '}
                    {businessInfo.parking}
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="glass rounded-2xl p-8">
              <h2 className="text-2xl font-bold font-['Playfair_Display'] text-[var(--gold)] mb-6">
                Bize Ulaşın
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Ad Soyad *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[var(--dark-bg)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--gold)] transition-colors duration-300"
                    placeholder="Adınız ve soyadınız"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Telefon *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[var(--dark-bg)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--gold)] transition-colors duration-300"
                    placeholder="05XX XXX XX XX"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    E-posta
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[var(--dark-bg)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--gold)] transition-colors duration-300"
                    placeholder="ornek@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                    Mesajınız *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-[var(--dark-bg)] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[var(--gold)] transition-colors duration-300 resize-none"
                    placeholder="Mesajınızı yazın..."
                  />
                </div>

                <button
                  type="submit"
                  className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-[var(--gold)] text-black font-bold rounded-lg hover:bg-[var(--gold-light)] transition-all duration-300 shadow-lg"
                >
                  <Send size={20} />
                  <span>WhatsApp ile Gönder</span>
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Form gönderildiğinde WhatsApp uygulamasına yönlendirileceksiniz.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-[var(--dark-card)]">
        <div className="container mx-auto px-4">
          <div className="glass rounded-2xl overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d506.47705655894875!2d29.093919369330673!3d40.95392222077594!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cac6fd4f588027%3A0x4e65bbd3facaf374!2sBostanc%C4%B1%20Hamam%20Spa!5e0!3m2!1str!2str!4v1707731234567!5m2!1str!2str"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Crocus Hamam Spa Konum - Bostancı Marmaray Durağı Karşısı"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;